import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiHelper } from '../lib/api';

export const timesheetKeys = {
    all: ['timesheets'],
    lists: () => [...timesheetKeys.all, 'list'],
    list: (orgId) => [...timesheetKeys.lists(), orgId],
    details: () => [...timesheetKeys.all, 'detail'],
    detail: (id) => [...timesheetKeys.details(), id],
};

// Fetch all timesheets
export function useTimesheets(orgId) {
    return useQuery({
        queryKey: timesheetKeys.list(orgId),
        queryFn: () => apiHelper.getResource('/timesheet/timesheets/', { orgId }),
        enabled: !!orgId,
    });
}

// Fetch or create monthly timesheet (main UI endpoint)
export function useMonthlyTimesheet(orgId, year, month, employeeId) {
    return useQuery({
        queryKey: [...timesheetKeys.all, 'monthly', orgId, year, month, employeeId],
        queryFn: () => apiHelper.getResource('/timesheet/timesheets/monthly/', {
            orgId,
            params: { year, month, employee_id: employeeId }
        }),
        enabled: !!orgId && year != null && month != null && !!employeeId,
    });
}

// Fetch single timesheet
export function useTimesheet(id, orgId) {
    return useQuery({
        queryKey: timesheetKeys.detail(id),
        queryFn: () => apiHelper.getResource(`/timesheet/timesheets/${id}/`, { orgId }),
        enabled: !!id,
    });
}

// Create timesheet (Initialize period)
export function useCreateTimesheet() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource('/timesheet/timesheets/', data, options);
        },
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({ queryKey: timesheetKeys.list(orgId) });
        },
    });
}

// Update timesheet (e.g. status submit)
export function useUpdateTimesheet() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.patchResource(`/timesheet/timesheets/${id}/`, data, options);
        },
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: timesheetKeys.list(orgId) });
            queryClient.invalidateQueries({ queryKey: timesheetKeys.detail(id) });
        },
    });
}

// --- Entries ---

// Create entry
export function useCreateTimesheetEntry() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource('/timesheet/timesheet-entries/', data, options);
        },
        onSuccess: () => {
            // Invalidate list to refresh totals/structure
            // We might need more granular invalidation if possible, but for now refresh all
            queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
        },
    });
}

// Update entry
export function useUpdateTimesheetEntry() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.patchResource(`/timesheet/timesheet-entries/${id}/`, data, options);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
        },
    });
}

// Delete entry
export function useDeleteTimesheetEntry() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.deleteResource(`/timesheet/timesheet-entries/${id}/`, options);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
        },
    });
}

// Save timesheet (validate and set status to SAVED)
export function useSaveTimesheet() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource(`/timesheet/timesheets/${id}/save/`, {}, options);
        },
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: timesheetKeys.list(orgId) });
            queryClient.invalidateQueries({ queryKey: timesheetKeys.detail(id) });
        },
    });
}

// Sign/Submit timesheet for approval
export function useSignTimesheet() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource(`/timesheet/timesheets/${id}/sign/`, {}, options);
        },
        onSuccess: (_, { id, orgId }) => {
            queryClient.invalidateQueries({ queryKey: timesheetKeys.list(orgId) });
            queryClient.invalidateQueries({ queryKey: timesheetKeys.detail(id) });
        },
    });
}

// Create timesheet line (project + activity)
export function useCreateTimesheetLine() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ timesheetId, data, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource(`/timesheet/timesheets/${timesheetId}/lines/`, data, options);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
        },
    });
}

// Upsert timesheet entries (bulk create/update hours)
export function useUpsertTimesheetEntries() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ timesheetId, data, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.patchResource(`/timesheet/timesheets/${timesheetId}/entries/`, data, options);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
        },
    });
}




// {
//   "entries": [
//     {
//       "line_id": "LINE-UUID-PROJECT-1",
//       "work_date": "2026-01-05",
//       "hours": 4.0,
//       "notes": "Morning: procurement review"
//     },
//     {
//       "line_id": "LINE-UUID-PROJECT-2",
//       "work_date": "2026-01-05",
//       "hours": 4.0,
//       "notes": "Afternoon: Data.FI dev"
//     },
//     {
//       "line_id": "LINE-UUID-PROJECT-1",
//       "work_date": "2026-01-06",
//       "hours": 8.0
//     },
//     {
//       "line_id": "LINE-UUID-PROJECT-1",
//       "work_date": "2026-01-07",
//       "hours": 8.0
//     },
//     {
//       "line_id": "LINE-UUID-PROJECT-2",
//       "work_date": "2026-01-08",
//       "hours": 8.0
//     }
//   ]
// }

// Fetch approved leaves for the timesheet period
// - No employeeId: fetches current user's leaves
// - With employeeId: fetches subordinate's leaves (for managers)
export function useMyApprovedLeaves(orgId, year, month, employeeId = null) {
    return useQuery({
        // The queryKey still includes them to ensure caching is unique
        queryKey: [...timesheetKeys.all, 'approved-leaves', orgId, year, month, employeeId],
        queryFn: () => {
            const params = {};
            // Only add year/month to params if they are actually provided
            if (year) params.year = year;
            if (month) params.month = month;
            if (employeeId) params.employee_id = employeeId;

            return apiHelper.getResource('/timesheet/my-approved-leaves/', { orgId, params });
        },
        enabled: !!orgId && !!employeeId && year != null && month != null,
    });
}

// Fetch holidays
// - No employeeId: fetches current user's holidays
// - With employeeId: fetches subordinate's holidays (for managers)
// - waitForEmployeeId: if true, waits until employeeId is available before fetching
export function useHolidays(orgId, employeeId = null, options = {}) {
    const { waitForEmployeeId = false } = options;
    return useQuery({
        queryKey: [...timesheetKeys.all, 'holidays', orgId, employeeId],
        queryFn: () => {
            const params = {};
            if (employeeId) params.employee_id = employeeId;
            return apiHelper.getResource('/timesheet/holidays/', { orgId, params });
        },
        enabled: !!orgId && (!waitForEmployeeId || !!employeeId),
    });
}

// Create holidays/time-off entries (bulk)
// For personal time off: { personal: true, holidays: [{date, name, hours, time_off_type}, ...] }
// For public holidays: { holidays: [{date, name, hours}, ...] }
export function useCreateHolidays() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource('/timesheet/holidays/', data, options);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
        },
    });
}

// Create a holiday date range
export function useCreateHolidayRange() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource('/timesheet/holidays/create-range/', data, options);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
        },
    });
}

// Delete a holidaythi(HR only)
export function useDeleteHoliday() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.deleteResource(`/timesheet/holidays/${id}/`, options);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
        },
    });
}

// --- Manager Timesheet Approval ---

// Fetch pending timesheets for manager approval
export function useManagerPendingTimesheets(orgId, { suppressToast = false } = {}) {
    return useQuery({
        queryKey: [...timesheetKeys.all, 'manager-pending', orgId],
        queryFn: () => apiHelper.getResource('/timesheet/manager/pending/', { orgId, suppressToast }),
        enabled: !!orgId,
        retry: false,
    });
}

// Approve timesheet (Line Manager)
export function useApproveTimesheet() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource(`/timesheet/timesheets/${id}/approve/`, {}, options);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
        },
    });
}

// --- HR Timesheet Approval ---

// Fetch timesheets pending HR approval
export function useHrPendingTimesheets(orgId) {
    return useQuery({
        queryKey: [...timesheetKeys.all, 'hr-pending', orgId],
        queryFn: () => apiHelper.getResource('/timesheet/hr/pending/', { orgId }),
        enabled: !!orgId,
    });
}

// HR final approve timesheet (status: hr_approved)
export function useHrApproveTimesheet() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource(`/timesheet/timesheets/${id}/hr-approve/`, {}, options);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
        },
    });
}

// Reject timesheet (Both Manager and HR use same endpoint)
export function useRejectTimesheet() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, reason, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource(`/timesheet/timesheets/${id}/reject/`, { reason }, options);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
        },
    });
}


// Download standard timesheet report (month or range) as Excel.
// GET /timesheet/download/monthly/?year=&month=[&employee_id=]
// GET /timesheet/download/range/?start_year=&start_month=&end_year=&end_month=[&employee_id=]
export function useDownloadStandardTimesheetReport() {
    return useMutation({
        mutationFn: ({ mode, year, month, startYear, startMonth, endYear, endMonth, employeeId, orgId }) => {
            const params = new URLSearchParams();
            let path;
            if (mode === 'range') {
                path = '/timesheet/download/range/';
                params.set('start_year', startYear);
                params.set('start_month', startMonth);
                params.set('end_year', endYear);
                params.set('end_month', endMonth);
            } else {
                path = '/timesheet/download/monthly/';
                if (year != null) params.set('year', year);
                if (month != null) params.set('month', month);
            }
            if (employeeId) params.set('employee_id', employeeId);
            const filename = mode === 'range'
                ? `Timesheet_${startYear}-${String(startMonth).padStart(2, '0')}_to_${endYear}-${String(endMonth).padStart(2, '0')}.xlsx`
                : `Timesheet_${year}-${String(month).padStart(2, '0')}.xlsx`;
            return apiHelper.downloadResource(`${path}?${params.toString()}`, filename, { orgId });
        },
        onSuccess: () => toast.success('Timesheet report downloaded.'),
    });
}

// Fetch all timesheets for HR with filters
export function useHrAllTimesheets(orgId, filters = {}) {
    return useQuery({
        queryKey: [...timesheetKeys.all, 'hr-all', orgId, filters],
        queryFn: () => apiHelper.getResource('/timesheet/hr/all/', { orgId, params: filters }),
        enabled: !!orgId,
    });
}
